if (!customElements.get('sv-promo-slider')) {
  class SvPromoSlider extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.viewport = this.querySelector('[data-viewport]');
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.autoplaySeconds = Math.max(3, parseInt(this.dataset.autoplay, 10) || 5);
      this.autoplayMs = this.autoplaySeconds * 1000;
      this.pauseOnHover = this.hasAttribute('data-pause-hover');
      this.timer = null;
      this.paused = false;
      this.style.setProperty('--sv-promo-autoplay', `${this.autoplaySeconds}s`);
      this.style.setProperty('--sv-promo-progress-state', 'paused');

      if (this.slides.length <= 1) return;

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i, true));
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.resume());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => {
          if (!this.contains(document.activeElement)) this.resume();
        });
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.resume();
      });

      let touchStartX = 0;
      this.viewport?.addEventListener(
        'touchstart',
        (event) => {
          touchStartX = event.changedTouches[0].screenX;
          this.pause();
        },
        { passive: true }
      );
      this.viewport?.addEventListener(
        'touchend',
        (event) => {
          const dx = event.changedTouches[0].screenX - touchStartX;
          if (Math.abs(dx) > 40) {
            if (dx < 0) this.next(true);
            else this.prev(true);
          }
          this.resume();
        },
        { passive: true }
      );

      this.goTo(this.index, false);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    restartProgress() {
      this.style.setProperty('--sv-promo-progress-state', 'paused');
      // Force animation restart on the active ring
      this.dots.forEach((dot) => {
        const ring = dot.querySelector('.sv-promo-slider__progress circle:last-child');
        if (!ring) return;
        ring.style.animation = 'none';
        void ring.offsetWidth;
        ring.style.animation = '';
      });
      if (!this.paused) {
        requestAnimationFrame(() => {
          this.style.setProperty('--sv-promo-progress-state', 'running');
        });
      }
    }

    start() {
      this.stop();
      if (this.slides.length <= 1 || this.paused) return;
      this.classList.add('is-playing');
      this.restartProgress();
      this.timer = window.setInterval(() => this.next(false), this.autoplayMs);
    }

    stop() {
      this.classList.remove('is-playing');
      this.style.setProperty('--sv-promo-progress-state', 'paused');
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      this.start();
    }

    prev(user) {
      this.goTo((this.index - 1 + this.slides.length) % this.slides.length, user);
    }

    next(user) {
      this.goTo((this.index + 1) % this.slides.length, user);
    }

    goTo(index, userInitiated) {
      if (!this.slides.length) return;
      this.index = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');

        // Restart Vastrakaala-style zoom-fit on the entering slide
        const media = slide.querySelector('.sv-promo-slider__image, .sv-promo-slider__fallback');
        if (!media) return;
        media.style.animation = 'none';
        if (active) {
          void media.offsetWidth;
          media.style.animation = '';
        }
      });
      this.dots.forEach((dot, i) => {
        const active = i === index;
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (userInitiated) {
        this.stop();
        this.start();
      } else if (!this.paused) {
        this.restartProgress();
      }
    }
  }

  customElements.define('sv-promo-slider', SvPromoSlider);
}
